import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getJobByPO } from '../utils/api';

/* Cover Sheet — the office's two paper cover sheets merged into one letter page.
   • From a record: Update Record → "Download Cover Sheet" (opens it filled in and
     brings up Print / Save as PDF), or type a PO here.
   • "Blank form" → the same page empty, to fill in by hand.
   Every payment line in the record prints as its own row: what it is, the service,
   amount, Due / Collected, the date received, cash / check / card and the check #.
   Loans print with lender, loan amount, dealer fee, what we receive, status and dates.
   Card details (bank, exp, CVV, account #) ALWAYS print blank — never stored.     */

const TYPE_ORDER = ['Deposit', 'Rem Collect', 'Start Collect', 'Complete Collect'];
const TYPE_LABEL = { 'Deposit': 'Deposit', 'Rem Collect': 'Upon Remeasure', 'Start Collect': 'Phase 1 (Start)', 'Complete Collect': 'Completion' };
const PRODUCTS = ['Roofing', 'Exterior Coating', 'Paint', 'Windows', 'Patio Doors', 'Gutters', 'Other'];
// Colours per product — the same family as the Job Tracker's product colours.
// acc = headings, acc2 = gradient end, mid = chips & totals, tint = panel backgrounds
const THEMES = {
  'Roofing':          { acc: '#2E6B1F', acc2: '#4C8B34', mid: '#C6E0B4', tint: '#EEF6E8' },
  'Exterior Coating': { acc: '#1F5F8B', acc2: '#3C86B8', mid: '#ADD8E6', tint: '#EAF5FA' },
  'Paint':            { acc: '#2F7FA8', acc2: '#59A3C8', mid: '#C9E4F2', tint: '#EEF7FC' },
  'Windows':          { acc: '#B3261E', acc2: '#D9534B', mid: '#FFB3AE', tint: '#FFF0EF' },
  'Patio Doors':      { acc: '#9A7B00', acc2: '#C29E10', mid: '#FFF1A8', tint: '#FFFBE6' },
  'Gutters':          { acc: '#B86A00', acc2: '#DB8B1C', mid: '#FFE0B2', tint: '#FFF6EA' },
  'Other':            { acc: '#6A1B9A', acc2: '#8E3CC0', mid: '#E3C2F3', tint: '#F8F0FC' },
};
const BLANK_THEME = { acc: '#2b2f33', acc2: '#4a5056', mid: '#e3e6e9', tint: '#F4F6F7' };
const STATUS = ['Sold', 'Remeasured', 'Scheduling', 'Schedule Confirmed', 'Completed', 'Funded'];
const PRODUCTION = ['Measure', 'Roof', 'Job Completion Form', 'Permit', 'Scope of Work', 'Material / Bill', 'Added to Job Tracker'];

const n = v => { const x = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(x) ? x : 0; };
const money = v => (n(v) ? (n(v) < 0 ? '−$' : '$') + Math.abs(n(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.00$/, '') : '');
function day(v) {
  if (!v) return '';
  const s = String(v).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${+m[2]}/${+m[3]}/${m[1]}` : s;
}
const iso = v => { const s = String(v || ''); if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const k = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(s); return k ? `${k[3].length === 2 ? '20' + k[3] : k[3]}-${k[1].padStart(2, '0')}-${k[2].padStart(2, '0')}` : ''; };
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
const isCollected = c => c.status === 'Collected' || (!c.status && !!c.payment_date);

// Which product(s) the job is — from the job's own service fields, contractors and Pre-Bid trade.
// Payment lines are NOT used: a payment filed under the wrong service group (e.g. the first one,
// Roofing) would otherwise tick a product the job doesn't have.
function productsOf(job) {
  const found = new Set();
  const add = s => {
    const t = String(s || '').toLowerCase();
    if (!t) return;
    if (t.includes('roof')) found.add('Roofing');
    else if (t.includes('coat') || t === 'ec') found.add('Exterior Coating');
    else if (t.includes('paint')) found.add('Paint');
    else if (t.includes('patio')) found.add('Patio Doors');
    else if (t.includes('win')) found.add('Windows');
    else if (t.includes('gut')) found.add('Gutters');
    else found.add('Other');
  };
  // The Service Type picked in each slot decides the product. The slots are labelled
  // Roof / EC / Win / Gut, but any service can be picked in any slot, so a contractor in
  // the "Roof" slot does NOT mean roofing — it's the contractor for whatever service is there.
  [job.svc1, job.svc2, job.svc3, job.svc4].forEach(add);
  if (job.prebid_trade) add(job.prebid_trade);
  // Only if no service type was picked at all: fall back to which contractor slots are filled.
  if (!found.size) {
    if (job.con_roof) found.add('Roofing');
    if (job.con_ec) found.add('Exterior Coating');
    if (job.con_win) found.add('Windows');
    if (job.con_gut) found.add('Gutters');
  }
  return found;
}

// Everything the sheet prints, worked out from the job record
function sheetData(job) {
  if (!job) return null;
  const cash = (job.cash_payments || []).filter(c => n(c.amount) || c.payment_date || c.status)
    .sort((a, b) => TYPE_ORDER.indexOf(a.payment_type) - TYPE_ORDER.indexOf(b.payment_type) || String(a.service).localeCompare(String(b.service)));
  const loans = (job.lender_payments || []).filter(l => l.lender_name || n(l.amount))
    .sort((a, b) => String(a.service).localeCompare(String(b.service)) || (a.lender_slot || 0) - (b.lender_slot || 0));
  const liveLoans = loans.filter(l => !['No Go', 'Cancelled'].includes(l.status));
  const sold = n(job.contract_amt);
  const finalPrice = n(job.change_order_amt) || sold;               // Change Order Amount = the job's new total
  const changeOrder = n(job.change_order_amt) ? finalPrice - sold : 0;
  const collected = cash.filter(isCollected).reduce((s, c) => s + n(c.amount), 0);
  const due = cash.filter(c => !isCollected(c) && c.status !== 'Waived' && c.status !== 'N/A').reduce((s, c) => s + n(c.amount), 0);
  const financed = liveLoans.reduce((s, l) => s + n(l.amount), 0);
  const fees = liveLoans.reduce((s, l) => s + n(l.dealer_fee), 0);
  const funded = liveLoans.filter(l => l.status === 'Funded').reduce((s, l) => s + n(l.amount) - n(l.dealer_fee), 0);
  const t = today();
  const status = {
    'Sold': !!(sold || job.contract_date),
    'Remeasured': !!(iso(job.rem_date) && iso(job.rem_date) <= t),
    'Scheduling': !!job.start_date,
    'Schedule Confirmed': false,
    'Completed': /complete/i.test(job.prod_status || '') || !!(iso(job.end_date) && iso(job.end_date) <= t),
    'Funded': /funded/i.test(job.prod_status || '') || loans.some(l => l.status === 'Funded') || !!String(job.funded || '').trim(),
  };
  const bids = (job.contractor_packets || []).filter(p => n(p.total_bid));
  const contractors = [job.con_roof, job.con_ec, job.con_win, job.con_gut].filter(Boolean);
  const expenses = [];
  if (n(job.contractor_invoice_amt)) expenses.push({ what: `Contractor invoice${contractors.length ? ' — ' + contractors.join(', ') : ''}`, amount: n(job.contractor_invoice_amt) });
  if (n(job.prepaid_bills)) expenses.push({ what: `Pre-paid bills${job.prepaid_notes ? ' — ' + job.prepaid_notes : ''}`, amount: n(job.prepaid_bills) });
  if (fees) expenses.push({ what: 'Dealer fees (kept by the lender)', amount: fees });
  const methods = cash.map(c => String(c.pay_method || '').toLowerCase());
  return {
    job, sold, changeOrder, finalPrice, cash, loans, collected, due, financed, fees, funded,
    remaining: finalPrice - collected - financed, status, products: productsOf(job),
    bid: bids.reduce((s, p) => s + n(p.total_bid), 0), bidFrom: bids.map(p => p.contractor_name).filter(Boolean).join(', '),
    invoice: n(job.contractor_invoice_amt), contractors: contractors.join(', '), expenses,
    production: {
      'Measure': day(job.rem_date), 'Permit': job.permit || '', 'Scope of Work': job.scope || '',
      'Material / Bill': job.materials || '', 'Added to Job Tracker': day(job.created_at),
    },
    pay: {
      check: methods.some(m => m.includes('check')) || cash.some(c => c.check_num),
      cash: methods.some(m => m === 'cash'),
      card: methods.some(m => /card|cc|credit|debit/.test(m)),
      finance: liveLoans.length > 0,
    },
  };
}

const CSS = `
.cs-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:16px}
.cs-bar .form-input{width:220px}
.cs-btn{border:1px solid #0D3B2E;background:#0D3B2E;color:#fff;border-radius:10px;padding:9px 16px;font-weight:700;font-size:13px;cursor:pointer}
.cs-btn.alt{background:#fff;color:#0D3B2E}
.cs-btn:disabled{opacity:.5;cursor:default}
.cs-msg{font-size:13px;color:#8E1B10}
.cs-stage{overflow-x:auto;padding-bottom:20px}
.cs-sheet{width:8.5in;min-height:11in;box-sizing:border-box;padding:.3in .4in;margin:0 auto;background:#fff;color:#111;
  box-shadow:0 4px 24px rgba(0,0,0,.12);font-family:Arial,Helvetica,sans-serif;font-size:9.5px;line-height:1.25}
.cs-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;padding-bottom:4px;border-bottom:3px solid var(--acc)}
.cs-type{display:inline-block;margin-top:3px;background:linear-gradient(90deg,var(--acc),var(--acc2));color:#fff;font-size:8.5px;font-weight:800;letter-spacing:.1em;padding:2px 8px;border-radius:10px}
.cs-co{font-weight:900;font-size:15px;color:var(--acc);letter-spacing:.02em}
.cs-co small{display:block;font-size:8.5px;font-weight:600;color:#555;letter-spacing:0}
.cs-job{background:var(--mid);padding:5px 12px;font-weight:900;font-size:12.5px;border:2px solid var(--acc);border-radius:6px;color:#111}
.cs-h{background:linear-gradient(90deg,var(--acc),var(--acc2));border-radius:3px;color:#fff;font-weight:800;text-align:center;letter-spacing:.12em;font-size:10.5px;padding:2px 0;margin:6px 0 4px}
.cs-h.big{font-size:13px;margin-top:4px}
.cs-checks{display:flex;justify-content:space-between;gap:6px;margin:3px 0 4px}
.cs-prodrow{display:flex;flex-wrap:wrap;gap:4px 10px;align-items:center;margin:2px 0 5px;padding:4px 6px;background:var(--tint);border:1.5px solid var(--acc);border-radius:5px;font-size:9.5px}
.cs-prodrow b{font-size:9px;letter-spacing:.06em;margin-right:4px}
.cs-prodrow > span{padding:1px 6px 1px 3px;border-radius:9px}
.cs-prodrow > .on{font-weight:900;color:#111;box-shadow:inset 0 0 0 1.3px rgba(0,0,0,.35)}
.cs-box{display:inline-block;width:11px;height:11px;border:1.3px solid #222;vertical-align:-2px;margin-right:4px;text-align:center;font-size:10px;line-height:10px;font-weight:900;background:#fff}
.cs-two{display:grid;grid-template-columns:1fr 1fr;gap:4px 18px}
.cs-l{display:flex;align-items:flex-end;gap:5px;min-height:15px}
.cs-l b{font-size:8.5px;font-weight:700;color:#333;white-space:nowrap}
.cs-l span{flex:1;border-bottom:1px solid #444;padding:0 3px 1px;font-size:10px;font-weight:600;color:#0a2a5e;min-height:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cs-pair{display:grid;grid-template-columns:1fr 1fr;gap:4px 10px}
.cs-pm{display:flex;gap:12px;font-size:9px;font-weight:700;margin-top:5px;flex-wrap:wrap}
.cs-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:3px 16px}
table.cs-t{width:100%;border-collapse:collapse;font-size:9px}
table.cs-t th{background:#e9ecef;border:1px solid #888;padding:2px 4px;font-size:7.5px;text-transform:uppercase;letter-spacing:.03em;text-align:left;white-space:nowrap}
table.cs-t td{border:1px solid #888;padding:1px 4px;height:14px;color:#0a2a5e;font-weight:600;white-space:nowrap}
table.cs-t td.lab{color:#222;font-weight:700;background:#f7f7f7}
table.cs-t td.r,table.cs-t th.r{text-align:right}
table.cs-t tr.tot td{background:#eef2f5;color:#111;font-weight:800}
table.cs-t.blue td{background:var(--tint)}
table.cs-t.blue td.lab{background:var(--mid)}
.cs-hl{background:var(--tint);border:2px solid var(--acc);border-radius:6px;padding:5px 8px;box-shadow:inset 0 0 0 1px rgba(255,255,255,.6)}
.cs-hl table.cs-t th{background:var(--acc);color:#fff;border-color:var(--acc)}
.cs-hl table.cs-t td{background:#fff}
.cs-hl table.cs-t td.lab{background:var(--tint)}
.cs-hl table.cs-t tr.tot td{background:var(--mid);color:#111;font-size:9.5px}
.cs-l.big b{font-size:10px;color:#111}
.cs-l.big span{font-size:13px;font-weight:900;color:#111;border-bottom:2px solid var(--acc)}
table.cs-t td .due{color:#B35C00;font-weight:800}
table.cs-t td .ok{color:#1B5E20;font-weight:800}
.cs-card{display:grid;grid-template-columns:1.4fr .7fr .6fr 1.3fr;gap:3px 10px;margin-top:3px}
.cs-cost{display:grid;grid-template-columns:1fr 1fr;gap:0 14px}
.cs-cost .k{border:1.3px solid #222;padding:4px 7px}
.cs-cost .k .t{font-weight:800;font-size:9px;letter-spacing:.06em;margin-bottom:2px}
.cs-prod{display:grid;grid-template-columns:1fr 1fr;gap:0 18px}
.cs-notes{border:1px solid #888;min-height:34px;padding:4px 6px;color:#0a2a5e;font-weight:600;white-space:pre-wrap;font-size:9.5px}
.cs-foot{display:flex;justify-content:space-between;font-size:7.5px;color:#777;margin-top:5px}
.cs-sheet.tight{font-size:8.5px}
.cs-sheet.tight table.cs-t td{height:12px;font-size:8.3px}
.cs-sheet.tight .cs-l{min-height:13px}
.cs-sheet.tight .cs-h{margin:4px 0 3px}
@media print{
  @page{size:letter portrait;margin:0}
  body *{visibility:hidden!important}
  .cs-sheet,.cs-sheet *{visibility:visible!important}
  .cs-stage{overflow:visible;padding:0}
  .cs-sheet{position:absolute;left:0;top:0;box-shadow:none;margin:0;height:11in;min-height:0;overflow:hidden}
  .cs-sheet,.cs-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  html,body{background:#fff!important}
}
`;

const L = ({ label, value, big }) => <div className={'cs-l' + (big ? ' big' : '')}><b>{label}</b><span>{value || ''}</span></div>;
const Box = ({ on }) => <span className="cs-box">{on ? '✓' : ''}</span>;
const statusCell = s => (s === 'Collected' || s === 'Funded' ? <span className="ok">{s}</span> : s === 'Due' || s === 'Pending' || s === 'Approved' ? <span className="due">{s}</span> : s || '');

export default function CoverSheet() {
  const [params, setParams] = useSearchParams();
  const [po, setPo] = useState(params.get('po') || '');
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  async function load(p, thenPrint) {
    const q = String(p || '').trim();
    if (!q) return;
    setLoading(true); setMsg('');
    try {
      const j = await getJobByPO(q);
      setJob(j); setPo(j.po_number || q); setParams({ po: j.po_number || q }, { replace: true });
      if (thenPrint) setTimeout(() => window.print(), 400);   // "Download Cover Sheet" from Update Record
    } catch (e) {
      setJob(null);
      setMsg(e.response?.status === 404 ? `No record found for PO ${q}.` : 'Could not load that record.');
    } finally { setLoading(false); }
  }
  useEffect(() => { if (params.get('po')) load(params.get('po'), params.get('print') === '1'); }, []); // eslint-disable-line

  function blank() { setJob(null); setPo(''); setMsg(''); setParams({}, { replace: true }); }

  const d = sheetData(job);
  const j = d?.job || {};
  const cashRows = d ? (d.cash.length ? d.cash : []) : TYPE_ORDER.map(t => ({ payment_type: t }));
  const loanRows = d ? d.loans : [{}, {}, {}];
  const tight = d && (d.cash.length + d.loans.length) > 8;
  // main product decides the sheet's colour (first in the list that the job has)
  const mainProduct = d ? PRODUCTS.find(p => d.products.has(p)) : null;
  const theme = (mainProduct && THEMES[mainProduct]) || BLANK_THEME;

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header">
        <h2>Cover Sheet</h2>
        <div style={{ fontSize: 13, color: '#888' }}>The job cover sheet on one page. Load a PO to fill it in from the record (or use “Download Cover Sheet” on Update Record), or print it blank. Admins only.</div>
      </div>
      <div className="page-body">
        <form className="cs-bar" onSubmit={e => { e.preventDefault(); load(po); }}>
          <input className="form-input" placeholder="PO # (e.g. 26-1364-1)" value={po} onChange={e => setPo(e.target.value)} />
          <button className="cs-btn alt" type="submit" disabled={loading || !po.trim()}>{loading ? 'Loading…' : 'Fill in from record'}</button>
          <button className="cs-btn alt" type="button" onClick={blank}>Blank form</button>
          <button className="cs-btn" type="button" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>🖨 Print / Save as PDF</button>
          {msg && <span className="cs-msg">{msg}</span>}
        </form>

        <div className="cs-stage">
          <div className={'cs-sheet' + (tight ? ' tight' : '')} style={{ '--acc': theme.acc, '--acc2': theme.acc2, '--mid': theme.mid, '--tint': theme.tint }}>
            <div className="cs-top">
              <div className="cs-co">CPR Home Solutions Inc<small>Project Cover Sheet</small>{mainProduct && <span className="cs-type">{mainProduct.toUpperCase()} PROJECT{d.products.size > 1 ? ` + ${d.products.size - 1} MORE` : ''}</span>}</div>
              <div className="cs-job">JOB # {j.po_number || '______________'}</div>
            </div>
            <div className="cs-h big">PROJECT COVER SHEET</div>
            <div className="cs-checks">
              {STATUS.map(s => <span key={s}><Box on={d?.status[s]} />{s}</span>)}
            </div>
            <div className="cs-prodrow">
              <b>PRODUCT TYPE:</b>
              {PRODUCTS.map(p => { const on = d?.products.has(p); return <span key={p} className={on ? 'on' : ''} style={on ? { background: THEMES[p].mid } : undefined}><Box on={on} />{p}</span>; })}
            </div>

            <div className="cs-two">
              <div>
                <L label="Sold Date:" value={day(j.contract_date)} />
                <L label="Customer Name:" value={j.customer_name} />
                <L label="Spouse:" value={j.customer_name_2} />
                <L label="Address:" value={j.address} />
                <div className="cs-pair"><L label="City:" value={j.city} /><L label="Zip:" value="" /></div>
                <L label="Email:" value={j.email} />
                <div className="cs-pair"><L label="Phone #1:" value={j.phone} /><L label="Phone #2:" value={j.phone_2} /></div>
              </div>
              <div>
                <div className="cs-pair"><L label="Team:" value="" /><L label="Lead Source:" value="" /></div>
                <div className="cs-pair"><L label="Canvasser:" value={[j.canvasser, j.canvasser_2].filter(Boolean).join(' / ')} /><L label="Telemarketer:" value={[j.telemarketer, j.telemarketer_2].filter(Boolean).join(' / ')} /></div>
                <div className="cs-pair"><L label="Sales Rep #1:" value={j.sales_rep} /><L label="Sales Rep #2:" value={j.sales_rep_2} /></div>
                <div className="cs-pair"><L label="Confirmer:" value={j.confirmer} /><L label="Marketer:" value="" /></div>
                <div className="cs-pair"><L label="Project Manager:" value="" /><L label="Contractor:" value={d?.contractors} /></div>
                <div className="cs-pm">
                  <span>PAYMENT METHOD:</span>
                  <span><Box on={d?.pay.check} />Check</span><span><Box on={d?.pay.cash} />Cash</span>
                  <span><Box on={d?.pay.card} />Credit Card</span><span><Box on={d?.pay.finance} />Finance</span>
                </div>
              </div>
            </div>

            <div className="cs-h">PRICING</div>
            <div className="cs-3 cs-hl">
              <div>
                <L label="Sold Amount:" value={money(d?.sold)} />
                <L label="Change Order (+/-):" value={d?.changeOrder ? (d.changeOrder > 0 ? '+' : '−') + money(Math.abs(d.changeOrder)) : ''} />
                <L big label="Final Price:" value={money(d?.finalPrice)} />
              </div>
              <div>
                <L label="Cash Collected:" value={d ? money(d.collected) || '$0' : ''} />
                <L label="Cash Still Due:" value={d ? money(d.due) || '$0' : ''} />
                <L label="Financed (loans):" value={d ? money(d.financed) || '$0' : ''} />
              </div>
              <div>
                <L label="Dealer Fees:" value={d ? money(d.fees) || '$0' : ''} />
                <L label="Funded to Us:" value={d ? money(d.funded) || '$0' : ''} />
                <L big label="Remaining Balance:" value={d ? money(d.remaining) || '$0' : ''} />
              </div>
            </div>

            <div className="cs-h">JOB COST — PRE-BID vs FINAL</div>
            <div className="cs-cost">
              <div className="k">
                <div className="t">PRE-BID (ESTIMATE)</div>
                <div className="cs-pair"><L label="Bid Amount:" value={money(d?.bid)} /><L label="Bid Sent:" value={day(j.bid_sent)} /></div>
                <L label="Contractor:" value={d?.bidFrom || d?.contractors} />
              </div>
              <div className="k">
                <div className="t">FINAL COST (INVOICE)</div>
                <div className="cs-pair"><L label="Invoice Amount:" value={money(d?.invoice)} /><L label="Received:" value={day(j.bid_rec)} /></div>
                <div className="cs-pair"><L label="Difference:" value={d && d.bid && d.invoice ? (d.invoice === d.bid ? 'Matches bid' : (d.invoice > d.bid ? 'Over by ' : 'Under by ') + money(Math.abs(d.invoice - d.bid))) : ''} /><L label="Approved By:" value="" /></div>
              </div>
            </div>

            <div className="cs-h">PAYMENT SCHEDULE — CASH / CHECK / CARD</div>
            <table className="cs-t blue">
              <thead><tr><th>Payment</th><th className="r">Amount</th><th>Due / Collected</th><th>Date Received</th><th>Cash / Check / Card</th><th>Check #</th></tr></thead>
              <tbody>
                {cashRows.map((c, i) => (
                  <tr key={i}>
                    <td className="lab">{TYPE_LABEL[c.payment_type] || c.payment_type || ''}</td>
                    <td className="r">{money(c.amount)}</td>
                    <td>{d ? statusCell(c.status || (c.payment_date ? 'Collected' : '')) : ''}</td>
                    <td>{day(c.payment_date)}</td>
                    <td>{c.pay_method || ''}</td>
                    <td>{c.check_num || ''}</td>
                  </tr>
                ))}
                {d && !d.cash.length && <tr><td className="lab" colSpan={6} style={{ color: '#777', fontWeight: 600 }}>No cash payments entered in the record</td></tr>}
                <tr className="tot"><td className="lab">Totals</td><td className="r">{d ? money(d.cash.reduce((s, c) => s + n(c.amount), 0)) : ''}</td>
                  <td colSpan={4}>{d ? `Collected ${money(d.collected) || '$0'} · Still due ${money(d.due) || '$0'}` : ''}</td></tr>
              </tbody>
            </table>

            <div className="cs-h">FINANCING</div>
            <div className="cs-hl">
            <table className="cs-t">
              <thead><tr><th>Lender</th><th className="r">Loan Amount</th><th className="r">Dealer Fee</th><th className="r">We Receive</th><th>Status</th><th>Approved</th><th>Funded</th></tr></thead>
              <tbody>
                {loanRows.map((l, i) => (
                  <tr key={i}>
                    <td className="lab">{l.lender_name || ''}</td>
                    <td className="r">{money(l.amount)}</td>
                    <td className="r">{money(l.dealer_fee)}</td>
                    <td className="r">{n(l.amount) ? money(n(l.amount) - n(l.dealer_fee)) : ''}</td>
                    <td>{d ? statusCell(l.status) : ''}</td>
                    <td>{day(l.approved_date)}</td>
                    <td>{day(l.funded_date)}</td>
                  </tr>
                ))}
                {d && !d.loans.length && <tr><td className="lab" colSpan={7} style={{ color: '#777', fontWeight: 600 }}>No financing on this job</td></tr>}
                <tr className="tot"><td className="lab">Totals</td><td className="r">{d?.loans.length ? money(d.financed) : ''}</td><td className="r">{d?.loans.length ? money(d.fees) || '$0' : ''}</td>
                  <td className="r">{d?.loans.length ? money(d.financed - d.fees) : ''}</td><td colSpan={3}>{d?.loans.length && d.financed ? `Dealer fee ${(d.fees / d.financed * 100).toFixed(1)}%` : ''}</td></tr>
              </tbody>
            </table>
            <div className="cs-card"><L label="Bank:" value="" /><L label="Exp:" value="" /><L label="CVV:" value="" /><L label="Acct #:" value="" /></div>
            </div>

            <div className="cs-h">EXPENSES INCURRED</div>
            <table className="cs-t">
              <thead><tr><th>Description</th><th className="r" style={{ width: 110 }}>Amount $</th></tr></thead>
              <tbody>
                {Array.from({ length: Math.max(3, (d?.expenses.length || 0) + 1) }).map((_, i) => { const e = d?.expenses[i]; return (
                  <tr key={i}><td>{e?.what}</td><td className="r">{money(e?.amount)}</td></tr>
                ); })}
                <tr className="tot"><td>Total expenses</td><td className="r">{d?.expenses.length ? money(d.expenses.reduce((s, e) => s + e.amount, 0)) : ''}</td></tr>
              </tbody>
            </table>

            <div className="cs-h">PRODUCTION</div>
            <div className="cs-prod">
              {PRODUCTION.map(p => <L key={p} label={p + ':'} value={d?.production[p]} />)}
            </div>

            <div className="cs-h">NOTES</div>
            <div className="cs-notes">{j.notes || ''}</div>

            <div className="cs-foot">
              <span>CPR Home Solutions Inc · CSLB #979774</span>
              <span>{j.po_number ? `Printed from CPR Live ${day(today())}` : 'Blank form'}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
